import type {ComponentProps, FC} from 'react';

import {Paragraph} from '@/modules/foundation/components/typography/paragraph';

import {ProductColorSelector} from '../../../product-color/ProductColorSelector';

import type {
	CabFloorMatMaterialConfiguration,
	CabFloorMatMaterialConfigurationValidationResult,
} from './configuration';
import {getUniqueMaterialColors, getUniqueStitchingColors} from './configuration';
import type {CabFloorMatMaterialCoreFragment} from './query';

type CabFloorMatColorSelectorProps = ComponentProps<'div'> & {
	materials: CabFloorMatMaterialCoreFragment[];
	namePrefix: string;
	defaultConfiguration?: CabFloorMatMaterialConfiguration;
	validationError?: CabFloorMatMaterialConfigurationValidationResult;
};

export const CabFloorMatMaterialSelector: FC<CabFloorMatColorSelectorProps> = ({
	materials,
	namePrefix,
	defaultConfiguration,
	validationError,
	...rest
}) => {
	if (materials.length < 1) {
		return null;
	}

	const configuredMaterial = materials.find(
		(material) =>
			material.materialColor?._id &&
			material.materialColor._id === defaultConfiguration?.materialColorId,
	);

	const materialColors = getUniqueMaterialColors(materials);
	const stitchingColors = configuredMaterial ? getUniqueStitchingColors(configuredMaterial) : [];

	return (
		<div {...rest}>
			{materialColors.length === 1 && (
				<input
					type="hidden"
					name={`${namePrefix}.materialColorId`}
					value={materialColors[0]._id}
				/>
			)}
			{materialColors.length > 1 && (
				<ProductColorSelector
					className="mb-3"
					legend="Materialfarge"
					namePrefix={`${namePrefix}.materialColorId`}
					colors={materialColors}
					defaultConfiguration={defaultConfiguration?.materialColorId ?? null}
					disableNoneSelection
				/>
			)}
			{validationError?.materialColorError?.message && (
				<Paragraph className="mt-3 text-red-600">
					{validationError?.materialColorError?.message}
				</Paragraph>
			)}
			{stitchingColors.length === 1 && (
				<input
					type="hidden"
					name={`${namePrefix}.stitchingColorId`}
					value={stitchingColors[0]._id}
				/>
			)}
			{stitchingColors.length > 1 && (
				<ProductColorSelector
					className="mt-8 mb-3"
					legend="Sømfarge"
					namePrefix={`${namePrefix}.stitchingColorId`}
					colors={stitchingColors}
					defaultConfiguration={defaultConfiguration?.stitchingColorId ?? null}
					disableNoneSelection
				/>
			)}
			{validationError?.stitchingColorError?.message && (
				<Paragraph className="mb-3 text-red-600">
					{validationError?.stitchingColorError?.message}
				</Paragraph>
			)}
			{validationError?.message && (
				<Paragraph className="mb-3 text-red-600">{validationError?.message}</Paragraph>
			)}
		</div>
	);
};
